import utils from '@cob/ui-genesis/cob.utils.js';
import AdvancedSearchP from './recordm.presenter.advancedsearch.js';
import SimpleSearchP from './recordm.presenter.simplesearch.js';

// The "Presenter" responsible for handling entity searches with simple search and advanced search.
//
// It always initiates with the simple search "Presenter" and
// swaps them when the user asks them to switch to the other search type.
//
// * `core`: a cob.app instance
// * `moduleBaseUri`: the base uri for this module
// * `$container` : the jquery HTML wrapper for the enclosing element
// * `definitionM`: the definitionM instance that you are using to search.
// * `simpleSearchM`: The Simple search Model
// * `advancedSearchM`: The Advanced search Model
var MultipleSearchP = function(core, moduleBaseUri, $container, definitionM, simpleSearchM, advancedSearchM, visualizationGroupM) {

    var _simpleSearchP = new SimpleSearchP(core, moduleBaseUri, definitionM, simpleSearchM, $container, {
                "advance-search": true
            },
            {
                goToAdvancedSearch: function() {
                    _goToAdvancedSearch();
                },
                getCurrentVisualization: function() {
                    return _getCurrentVisualization();
                },
                updateUri: function(newState) {
                    _updateUri(newState)
                }
            }),
        _advancedSearchP = new AdvancedSearchP(core, moduleBaseUri, definitionM, advancedSearchM, $container, {
            goToSimpleSearch: function() {
                _goToSimpleSearch();
            },
            getCurrentVisualization: function() {
                return _getCurrentVisualization();
            },
            updateUri: function(newState) {
                _updateUri(newState)
            }
        });

    var _currentP = _simpleSearchP,
        _isAdvancedQuery,
        _activeVisualizationId;

    var _goToAdvancedSearch = function() {
        _simpleSearchP.pause();
        _advancedSearchP.resume();

        _currentP = _advancedSearchP;
    };

    var _goToSimpleSearch = function() {
        _advancedSearchP.pause();
        _simpleSearchP.resume();

        _currentP = _simpleSearchP;
    };

    var _getCurrentVisualization = function() {
        return visualizationGroupM.get(_activeVisualizationId);
    };

    var _updateSelectedVisualization = function(e, visualizationId) {
        _activeVisualizationId = visualizationId;
    };

    const _setQueryType = function(query) {
        if (query !== undefined) {

            // it means it's starting for the first time,
            _isAdvancedQuery = query && typeof query !== "string";

            _currentP = _isAdvancedQuery ? _advancedSearchP : _simpleSearchP;
        }
    };

    /**
     * newPartialState: An object containing the possible properties
     * - searchText: For a simple search
     * - searchParameters: for an advance search
     * - selectedLabels: {label: "a label", selected: true or false}
     * @param newPartialState
     * @private
     */
    const _updateUri = function(newPartialState) {
        const currentView = _getCurrentVisualization();
        let uri;

        if (_currentP.name === 'simple-search') {
            uri = definitionM.buildUriForQuery(newPartialState.searchText ?? simpleSearchM.getQuery());
        } else {
            uri = definitionM.buildUriForAdvSearch(JSON.stringify(newPartialState.searchParameters ?? advancedSearchM.getQuery()));
        }

        const viewId = currentView ? currentView.getId() : "-1"

        // Let's only add the parameter av if the value is different from -1
        if (viewId !== "-1") {
            uri += `&av=${viewId}`
        }

        if (_currentP.name === "simple-search") {
            const filters = (newPartialState.selectedLabels || simpleSearchM.getFilters())
                .filter(function(f) {
                    return f.selected !== f.defaultValue
                })
                .reduce(function(dict, f) {
                    dict[f.label] = f.selected
                    return dict
                }, {})

            // If the filters object is empty then don't add the parameter to the url
            if (Object.keys(filters).length !== 0) {
                // If no visualization is active we send the value -1 because we need to make sure that all arguments
                // are passed when initializing the constructor of the module
                // I just want to add the view parameter if there isn't any already
                if (uri.indexOf("&av=") === -1) {
                    uri += `&av=${viewId}`
                }
                uri += `&filters=${JSON.stringify(filters)}`
            }
        }

        window.console.log("Changing URI for search definition: ", uri);

        if (core.isInIframe() && utils.userSession.getQueryParameter("no_history")) {
            window.console.debug("In iframe without history ", history);
            location.replace("#/" + uri);

        } else {
            core.navigateTo(uri);
        }
    }

    var _init = function(query, visualizationId) {

        core.subscribe("recordm:search-view-changed:" + definitionM.getSettingKey(), _updateSelectedVisualization);

        _activeVisualizationId = visualizationId;

        _setQueryType(query);

        _currentP.init(query);
    };

    var _resume = function() {
        if (_isAdvancedQuery) {
            _goToAdvancedSearch();

        } else {
            _goToSimpleSearch();
        }

        _currentP.refresh();

        core.subscribe("recordm:search-view-changed:" + definitionM.getEntityKey(), _updateSelectedVisualization);
    };

    var _pause = function() {
        core.unsubscribe("recordm:search-view-changed:" + definitionM.getEntityKey(), _updateSelectedVisualization);
        _currentP.pause();
    };

    var _destroy = function() {
        core.unsubscribe("recordm:search-view-changed:" + definitionM.getEntityKey(), _updateSelectedVisualization);

        _simpleSearchP.destroy();
        _advancedSearchP.destroy();
    };

    return {
        init: _init,
        resume: _resume,
        pause: _pause,
        destroy: _destroy,
        prepareView: function() {
            _currentP.prepareView();
        },
        setQuery: function(query) {
            _setQueryType(query);
            _currentP.doSearch(query);
            _currentP.refresh();
        },
        refresh: _currentP.refresh
    };

};

export default MultipleSearchP;
